import "./new-employee-loan.scss";

import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { Button } from "primereact/button";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { useQyCreateEmployeeLoan } from "@core/query/employee-loan.query";
import {
  EmployeeAttachmentFormSchema,
  EmployeeLoanFormRule,
  EmployeeLoanFormSchema,
} from "@core/form/form.rule";
import { employeeLoanFormDefault } from "@core/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import HeaderContent from "@shared/ui/header-content/header-content";
import FormEmployeeLoan from "../form-employee-loan/form-employee-loan";
import { useFormDropdownPersonContext } from "@domain/person/form-dropdown-person/form-dropdown-person.context";
import { useQyCreateEmployeeAttachment } from "@core/query/employee-attachment.query";
import { AttachmentSource } from "@core/model/query-model-employee-attachment";

export function NewEmployeeLoan() {
  // LOCAL DECLARATION
  const { selectedPersonData } = useFormDropdownPersonContext();
  const navigate = useNavigate();
  const { mapCreateEmployeeLoan, mapCreateEmployeeAttachment } =
    useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  // API ATTACHMENT
  const { mutate: addEmployeeAttachment } = useQyCreateEmployeeAttachment();

  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    if (typeof fileInput === "object") {
      const mutatedForm = {
        userCode: selectedPersonData?.code,
        bucketCode: selectedPersonData?.bucket.code,
        isActive: true,
        source: AttachmentSource.Loan,
        file: fileInput,
        requestCode: data.code,
      } as EmployeeAttachmentFormSchema;

      const formData = mapCreateEmployeeAttachment(mutatedForm);
      addEmployeeAttachment(formData);
    }

    showSuccess(`New EmployeeLoan is created`);
    navigate(`../`);
  };
  const { mutate: addEmployeeLoan, isLoading: isCreating } =
    useQyCreateEmployeeLoan(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeeLoanFormSchema>({
    defaultValues: employeeLoanFormDefault,
    resolver: zodResolver(EmployeeLoanFormRule),
  });
  const { handleSubmit, watch } = formMethod;
  const fileInput = watch("file");

  const handleValidate = (form: EmployeeLoanFormSchema) => {
    const updatedForm = {
      ...form,
      bucketCode: selectedPersonData?.bucket.code,
      userCode: selectedPersonData?.code,
    } as EmployeeLoanFormSchema;
    const formData = mapCreateEmployeeLoan(updatedForm);
    addEmployeeLoan(formData);
  };
  const handleValidateError = (err: FieldErrors<EmployeeLoanFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return (
    <div id="EmployeeLoan" className="employee-loan">
      <HeaderContent
        title="New Employee Loan"
        backIcon={<FaChevronLeft />}
        onBack={() => navigate("../")}
      >
        <Button
          className="w-full block"
          label="Save"
          disabled={isCreating}
          onClick={handleSubmit(handleValidate, handleValidateError)}
        ></Button>
      </HeaderContent>

      <div className="p-7">
        <FormProvider {...formMethod}>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 bg-white">
            <FormEmployeeLoan />
          </section>
        </FormProvider>
      </div>
    </div>
  );
}

export default NewEmployeeLoan;
